/**
 * The calendar action registry — the ordered, closed list of everything the
 * context menu (and the hotkey layer) can do to a calendar event.
 *
 * ADDING AN ACTION = one file in actions/ + one line here. Visibility,
 * grouping, hotkey and label all live in the action object; nothing else in
 * the system needs to change.
 */
import { MCEvent } from "../model/types";
import { buildActionContext } from "./context";
import {
  ActionContext,
  ActionServices,
  CalendarAction,
} from "./types";
import {
  cancelAppointment,
  chooseTherapyMethod,
  editAppointment,
  editCancellationReason,
  moveAppointment,
  moveSingleSlot,
  openAppointmentOverview,
  openNotes,
  openPatient,
  openPrescription,
  setInformed,
  toggleAttended,
} from "./appointment";
import {
  deleteAbsence,
  deleteBreak,
  deleteGroupTherapist,
  deleteNamedEvent,
  deleteTravel,
  editEventTitle,
  resetCancelledDay,
  resetWorkingHoursException,
} from "./timeslots";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CALENDAR_ACTIONS: ReadonlyArray<CalendarAction<any>> = [
  // navigate
  openPatient,
  openAppointmentOverview,
  openPrescription,
  openNotes,
  // modify
  moveAppointment,
  moveSingleSlot,
  editAppointment,
  chooseTherapyMethod,
  toggleAttended,
  setInformed,
  editCancellationReason,
  editEventTitle,
  // danger
  cancelAppointment,
  deleteNamedEvent,
  deleteBreak,
  deleteTravel,
  deleteAbsence,
  resetCancelledDay,
  resetWorkingHoursException,
  deleteGroupTherapist,
];

/** Visible actions for a context, in registry order. */
export const actionsForContext = (ctx: ActionContext): CalendarAction[] =>
  CALENDAR_ACTIONS.filter((action) => action.when(ctx)) as CalendarAction[];

/**
 * Run one registry action against an engine event (the hotkey layer's entry
 * point — same context build, same visibility gate as the menu). Returns
 * false when the action does not apply to the event.
 */
export const runCalendarAction = (
  actionId: string,
  event: MCEvent,
  services: ActionServices,
): boolean => {
  const ctx = buildActionContext(event);
  if (!ctx) return false;
  const action = CALENDAR_ACTIONS.find((a) => a.id === actionId);
  if (!action || !action.when(ctx)) return false;
  void action.run(ctx as never, services);
  return true;
};

export { buildActionContext } from "./context";
export * from "./types";
